import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { graphql } from 'gatsby';
import { getSrc } from 'gatsby-plugin-image';
import {
    Container,
    className,
    Text,
    Heading,
    Row,
    Col,
    Card,
} from 'design-system';
import { useTranslate } from 'gatsby-theme-soluble-source';
import ButtonComponent from '../ButtonComponent';
import style from './styles.module.scss';

let y = 0;

export default function NewsSection({
    posts,
    title,
    pretitle,
    pt,
    postsPerPage = 3,
    previousSectionType,
}) {
    const translation = useTranslate();

    const [page, setPage] = useState(1);
    const [currentPost, setCurrentPost] = useState(
        posts?.length ? posts.slice(0, page * postsPerPage) : [],
    );
    const [hasMore, setHasMore] = useState(true);

    const loadMore = useCallback(() => {
        y = window.scrollY;

        setCurrentPost(
            posts?.length ? posts.slice(0, (page + 1) * postsPerPage) : [],
        );
        setPage(page + 1);
    }, [setCurrentPost, setPage, page, postsPerPage, posts]);

    useEffect(() => {
        if (page > 1) {
            window.scrollTo(window.scrollX ? window.scrollX : 0, y);
        }

        if (!posts?.length || page * postsPerPage >= posts.length) {
            setHasMore(false);
        }
    }, [page, setHasMore, posts, postsPerPage]);

    const finalPt = useMemo(() => {
        if (pt) {
            return pt;
        }

        if (previousSectionType === 'DatoCmsHeroSection' && !title) {
            return { xs: 14, lg: 20 };
        }

        if (previousSectionType === 'DatoCmsSectorsSection') {
            return {};
        }

        if (previousSectionType === 'DatoCmsTextImageSection') {
            return { xs: 10 };
        }

        return { xs: 20, lg: 30 };
    }, [title, previousSectionType, pt]);

    return (
        <section data-datocms-noindex>
            <Container pt={finalPt} pb={{ xs: 15, lg: 25 }}>
                <Row>
                    {title || pretitle ? (
                        <Col className="text-center">
                            {pretitle ? (
                                <Text
                                    tag="p"
                                    className={`d-block mb-3 subtitle text-uppercase font-weight-bold ${style.pretitle}`}>
                                    {pretitle}
                                </Text>
                            ) : null}
                            {title ? (
                                <Heading
                                    className="h1"
                                    tag="h3"
                                    mb={{ xs: 10 }}>
                                    {title}
                                </Heading>
                            ) : null}
                        </Col>
                    ) : null}
                    <Col offset={{ lg: 1 }} col={{ lg: 10 }}>
                        <Row>
                            {currentPost.map((post, index) => {
                                return (
                                    <Col
                                        key={index}
                                        col={{ lg: 4, md: 6 }}
                                        pb={{ xs: 5 }}>
                                        <Card
                                            wrapperClassName={`shadow ${style.card}`}
                                            imgTop={
                                                post?.featuredImage
                                                    ?.gatsbyImageData
                                                    ? getSrc(post.featuredImage)
                                                    : null
                                            }
                                            imgTopPlaceholderClassName={
                                                style['image-placeholder']
                                            }
                                            bodyClassName={style['card__body']}
                                            preTitleComponent={
                                                <Text className="font-weight-bold subtitle text-uppercase text-primary mb-3 d-block">
                                                    {`${
                                                        post.authors?.length
                                                            ? post.authors
                                                                  .map(
                                                                      (a) =>
                                                                          a.name,
                                                                  )
                                                                  .join(', ')
                                                            : 'Nae'
                                                    } | ${
                                                        post.meta
                                                            ?.firstPublishedAt
                                                            ? post.meta
                                                                  .firstPublishedAt
                                                            : post.defaultMeta
                                                                  .firstPublishedAt
                                                    }`}
                                                </Text>
                                            }
                                            title={post.title}
                                            titleHref={post.slug}
                                            titleClassName={`h5 font-weight-bold ${style['card__title']}`}
                                            text={post.excerpt}
                                            textClassName={`h6 ${style['card__text']}`}>
                                            <div {...className('pb-8')} />
                                            <div
                                                className={
                                                    style['button-wrapper']
                                                }>
                                                <ButtonComponent
                                                    button={{
                                                        type: 'secondary',
                                                        label: translation[
                                                            'news-section.new.cta'
                                                        ].text,
                                                        link: {
                                                            slug: post.slug,
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Col>
                    {hasMore ? (
                        <Col
                            pt={{ xs: 2, lg: 10 }}
                            pb={{ xs: 5 }}
                            className="text-center">
                            <ButtonComponent
                                button={{
                                    label: translation['news-section.more']
                                        .text,
                                    type: 'primary-xl',
                                    Tag: 'button',
                                    onClick: loadMore,
                                }}
                            />
                        </Col>
                    ) : null}
                </Row>
            </Container>
        </section>
    );
}

export const query = graphql`
    fragment PostItem on DatoCmsNews {
        slug
        title
        excerpt
        defaultMeta: meta {
            firstPublishedAt(locale: "en", formatString: "L")
        }
        authors {
            name
        }
        featuredImage {
            alt
            gatsbyImageData(
                imgixParams: { w: "720", h: "384", fit: "crop" }
                placeholder: BLURRED
                layout: FIXED
            )
        }
    }

    fragment DatoCmsNewsSection on DatoCmsNewsSection {
        title
        postsPerPage
        posts {
            ...PostItem
        }
    }
`;
